/**
 * GroupingManager - Handles element grouping and ungrouping
 */

import { EventEmitter } from '../core/EventEmitter';
import { EditableElement } from '../editing/types';
import { 
  groupElements, 
  ungroupElements, 
  isGroup, 
  getGroupChildren,
  updateGroup,
  calculateBounds,
} from '../editing/GroupManager';

export class GroupingManager {
  private emitter: EventEmitter;
  
  // Callbacks
  private onGroupChange: ((elements: EditableElement[], groupId?: string, ungroupedIds?: string[]) => void) | null = null;

  constructor(emitter: EventEmitter) {
    this.emitter = emitter;
  }

  /**
   * Set callback for group changes
   */
  setOnGroupChange(callback: (elements: EditableElement[], groupId?: string, ungroupedIds?: string[]) => void): void {
    this.onGroupChange = callback;
  }

  /**
   * Group selected elements
   */
  group(elements: EditableElement[], selectedIds: string[]): { elements: EditableElement[]; groupId: string } | null {
    if (selectedIds.length < 2) {
      console.warn('At least 2 elements must be selected to create a group');
      return null;
    }
    
    try {
      const result = groupElements(elements, selectedIds);
      
      if (this.onGroupChange) {
        this.onGroupChange(result.elements, result.groupId);
      }
      
      return result;
    } catch (error) {
      console.error('Group error:', error);
      return null;
    }
  }

  /**
   * Ungroup a group element
   */
  ungroup(elements: EditableElement[], groupId: string): { elements: EditableElement[]; ungroupedIds: string[] } | null {
    const group = elements.find((el) => el.id === groupId);
    
    if (!group || !isGroup(group)) {
      console.warn('Element is not a valid group');
      return null;
    }
    
    try {
      const result = ungroupElements(elements, groupId);
      
      if (this.onGroupChange) {
        this.onGroupChange(result.elements, undefined, result.ungroupedIds);
      }
      
      return result;
    } catch (error) {
      console.error('Ungroup error:', error);
      return null;
    }
  }

  /**
   * Check if an element is a group
   */
  isGroup(element: EditableElement): boolean {
    return isGroup(element);
  }

  /**
   * Get children of a group
   */
  getGroupChildren(group: EditableElement): string[] {
    return getGroupChildren(group);
  }

  /**
   * Update group bounds when children change
   */
  updateGroup(group: EditableElement, childUpdates: Map<string, { x: number; y: number }>): EditableElement {
    return updateGroup(group, childUpdates);
  }

  /**
   * Calculate bounding box for multiple elements
   */
  calculateBounds(elements: EditableElement[]): { x: number; y: number; width: number; height: number } {
    return calculateBounds(elements);
  }

  /**
   * Destroy the grouping manager
   */
  destroy(): void {
    this.onGroupChange = null;
  }
}
